import React, { useMemo, useState } from "react";
import camelCase from "lodash/camelCase";
import { AnyPlainObject } from "arcjs/common/misc";

export type ListViewProps = React.HTMLProps<HTMLDivElement> & {
    horizontal?: boolean;
};

export const MagicFixGhostElementForAlignment = ({ ghostClassName = "", ghostStyles = {} }) => {
    return (
        <>
            {Array.from({ length: 20 }, (_, i) => (
                <div
                    key={i}
                    aria-hidden={true}
                    className={`${ghostClassName}`}
                    style={{
                        ...ghostStyles,
                        height: 0,
                        minHeight: 0,
                        paddingTop: 0,
                        paddingBottom: 0,
                    }}
                ></div>
            ))}
        </>
    );
};

const plantRefOnFirstReactElement = (
    children: React.ReactNode,
    ref: React.Ref<any>
): React.ReactNode => {
    const childrenList = React.Children.toArray(children);

    for (let i = 0; i < childrenList.length; i++) {
        const child = childrenList[i];
        if (React.isValidElement(child)) {
            childrenList[i] = React.cloneElement(child as React.ReactElement, { ref });
            break;
        }
    }

    return childrenList;
};

const addFlexGrowToChildren = (children: React.ReactNode) => {
    const childrenList = React.Children.toArray(children);

    for (let i = 0; i < childrenList.length; i++) {
        const child = childrenList[i];
        if (React.isValidElement(child)) {
            const existingStyle = (child as React.ReactElement<HTMLElement>).props.style;
            childrenList[i] = React.cloneElement(child as React.ReactElement, {
                style: { ...existingStyle, flexGrow: "1" },
            });
        }
    }

    return childrenList;
};

const getStyles = (element: HTMLElement) => {
    if (!element) return {};
    const existingStyles = element.style;
    const styles: AnyPlainObject = {};
    for (let i = 0; i < existingStyles.length; i++) {
        const name = existingStyles[i];
        styles[camelCase(name)] = existingStyles.getPropertyValue(name);
    }
    return styles;
};

/**
 * Component that displays a fluid list. Items placed inside are by default
 * placed in rows and scrolled vertically. If `props.horizontal` is true,
 * items are displayed in 1 row, scrolled horizontally.
 * Due to limitations in implementation, all React Element children must:
 * 1. forwardRef;
 * 2. has a well-defined css `width`
 */
const FluidListView = ({ children, horizontal = false }: ListViewProps) => {
    const scrollClass = horizontal
        ? "scroll-horizontal h-fit space-x-4"
        : "overflow-y-scroll flex flex-wrap gap-4";
    const [ghostClassName, setGhostClassName] = useState("");
    const [ghostStyles, setGhostStyles] = useState<AnyPlainObject>({});

    const childrenToRender = useMemo(() => {
        const growAddedChildren = addFlexGrowToChildren(children);

        // Get the exact css classes applied on children elements
        // to use on ghost elements
        // ghost elements keep the children last flex row consistently-sized as the ones above
        // the catch is, children need to accept ref, which means forwardRef has to be used for functional components
        const refPlantedChildren = plantRefOnFirstReactElement(growAddedChildren, (ref) => {
            if (!ref) return;
            setGhostClassName((ref as HTMLElement)?.className || "");
            setGhostStyles(getStyles(ref));
        });

        return refPlantedChildren;
    }, [children]);

    return (
        <div className={`${scrollClass}`}>
            {childrenToRender}
            {!horizontal && (
                <MagicFixGhostElementForAlignment
                    ghostClassName={ghostClassName}
                    ghostStyles={ghostStyles}
                />
            )}
        </div>
    );
};

export default FluidListView;
